import type { FC, ReactNode } from 'react'
import type { Wallpaper } from '@/types'

import { useRef } from 'react'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { WallpaperCard } from '@/components/wallpapers/wallpaper-card'

interface PhoneRowProps {
    wallpapers: Array<Wallpaper>
    label: string
    heading: ReactNode
    action?: ReactNode
}

const arrowClasses = 'hidden size-10 cursor-pointer items-center justify-center rounded-full border border-white/10 bg-white/[0.04] text-foreground transition hover:bg-white/10 focus-visible:ring-3 focus-visible:ring-ring/50 focus-visible:outline-none md:inline-flex'

// A swipeable shelf of phone wallpapers, with arrow buttons for mouse users.
export const PhoneRow: FC<PhoneRowProps> = ({ wallpapers, label, heading, action }): ReactNode => {
    const listRef = useRef<HTMLUListElement>(null)

    const scroll = (direction: 1 | -1) => {
        const list = listRef.current

        if (!list) return

        const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches

        list.scrollBy({ left: direction * list.clientWidth * 0.85, behavior: reduceMotion ? 'auto' : 'smooth' })
    }

    return (
        <div>
            <div className='flex flex-wrap items-end justify-between gap-x-6 gap-y-4'>
                {heading}

                <div className='flex items-center gap-2'>
                    {action}

                    <button type='button' onClick={() => scroll(-1)} className={arrowClasses}>
                        <ChevronLeft aria-hidden='true' className='size-5' />
                        <span className='sr-only'>Scroll back</span>
                    </button>

                    <button type='button' onClick={() => scroll(1)} className={arrowClasses}>
                        <ChevronRight aria-hidden='true' className='size-5' />
                        <span className='sr-only'>Scroll forward</span>
                    </button>
                </div>
            </div>

            <ul
                ref={listRef}
                aria-label={label}
                className='no-scrollbar -mx-4 mt-8 flex snap-x snap-mandatory gap-3 overflow-x-auto scroll-px-4 px-4 pb-2 sm:-mx-6 sm:scroll-px-6 sm:gap-4 sm:px-6 lg:-mx-10 lg:scroll-px-10 lg:px-10'
            >
                {wallpapers.map(wallpaper => (
                    <li key={wallpaper.id} className='w-[42vw] max-w-56 shrink-0 snap-start sm:w-48 lg:w-56'>
                        <WallpaperCard
                            wallpaper={wallpaper}
                            aspect='phone'
                            sizes='(min-width: 1024px) 224px, (min-width: 640px) 192px, 42vw'
                        />
                    </li>
                ))}
            </ul>
        </div>
    )
}
